import type { PrismaClient } from "@calcom/prisma/client";
import { isSelfieRoomServiceEventType, SELFIE_ROOM_STRIPE_MINIMUM_CENTS } from "./selfieroomPromoCodes";

export const SELFIE_ROOM_REFERRAL_METADATA_KEY = "selfieroomReferralCode";

export type SelfieRoomReferralDiscount = {
  code: string;
  referralCodeId: string;
  referrerEmail: string;
  referrerName?: string | null;
  friendDiscountValue: number;
  rewardAmount: number;
  originalAmount: number;
  discountedAmount: number;
  currency: string;
};

export const normalizeSelfieRoomReferralCode = (value: string) =>
  value
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9_-]/g, "");

export const buildSelfieRoomReferralLink = (code: string) =>
  `/selfieroom?ref=${encodeURIComponent(normalizeSelfieRoomReferralCode(code))}`;

export const resolveSelfieRoomReferralDiscount = async ({
  prisma,
  rawCode,
  eventTypeId,
  bookerEmail,
  amount,
  currency,
}: {
  prisma: PrismaClient;
  rawCode: string | undefined;
  eventTypeId: number | null | undefined;
  bookerEmail: string;
  amount: number;
  currency: string;
}): Promise<SelfieRoomReferralDiscount | null> => {
  const code = normalizeSelfieRoomReferralCode(rawCode ?? "");
  if (!code) return null;

  if (!(await isSelfieRoomServiceEventType({ prisma, eventTypeId }))) return null;

  const referralCode = await prisma.selfieRoomReferralCode.findUnique({
    where: { code },
    select: {
      id: true,
      code: true,
      customerName: true,
      customerEmail: true,
      active: true,
      rewardAmount: true,
      friendDiscountAmount: true,
    },
  });

  if (!referralCode?.active) return null;

  if (referralCode.customerEmail.trim().toLowerCase() === bookerEmail.trim().toLowerCase()) return null;

  const friendDiscountValue = Math.min(
    referralCode.friendDiscountAmount,
    Math.max(0, amount - SELFIE_ROOM_STRIPE_MINIMUM_CENTS)
  );

  if (friendDiscountValue <= 0) return null;

  return {
    code: referralCode.code,
    referralCodeId: referralCode.id,
    referrerEmail: referralCode.customerEmail,
    referrerName: referralCode.customerName,
    friendDiscountValue,
    rewardAmount: referralCode.rewardAmount,
    originalAmount: amount,
    discountedAmount: amount - friendDiscountValue,
    currency,
  };
};
